export interface CaptionCueTiming {
  startTime: number;
  endTime: number;
}

export interface CaptionLineRect {
  top: number;
  bottom: number;
}

export interface CaptionPage {
  offset: number;
  contentHeight: number;
}

/** Page breaks follow actual rendered line boxes, including browser wrapping and CJK. */
export const buildCaptionPages = (
  lineRects: CaptionLineRect[],
  viewportHeight: number,
): CaptionPage[] => {
  const lines = lineRects
    .filter((rect) => Number.isFinite(rect.top) && Number.isFinite(rect.bottom) && rect.bottom > rect.top)
    .sort((a, b) => a.top - b.top);
  if (lines.length === 0 || viewportHeight <= 0) return [{ offset: 0, contentHeight: 0 }];

  const firstTop = lines[0].top;
  let pageTop = firstTop;
  let pageBottom = firstTop;
  const pages: CaptionPage[] = [];
  lines.forEach((line) => {
    const renderedPageHeight = line.bottom - pageTop + firstTop;
    if (renderedPageHeight > viewportHeight && line.top > pageTop && pageBottom > pageTop) {
      pages.push({
        offset: Math.max(0, Number((pageTop - firstTop).toFixed(3))),
        contentHeight: Number((pageBottom - pageTop + firstTop).toFixed(3)),
      });
      pageTop = line.top;
    }
    pageBottom = Math.max(pageBottom, line.bottom);
  });
  pages.push({
    offset: Math.max(0, Number((pageTop - firstTop).toFixed(3))),
    // A single line may be taller than the nominal budget. Keep its complete
    // measured box visible rather than clipping the glyphs to the budget.
    contentHeight: Number((pageBottom - pageTop + firstTop).toFixed(3)),
  });
  return pages;
};

/** Choose a cue page from video time, so pause, seek, and replay stay deterministic. */
export const getCaptionPageIndex = (
  cues: CaptionCueTiming[],
  currentTime: number,
  pageCount: number,
): number => {
  const lastPage = Math.max(0, pageCount - 1);
  if (lastPage === 0 || cues.length === 0 || !Number.isFinite(currentTime)) return 0;

  const startTime = Math.max(...cues.map((cue) => cue.startTime));
  const endTime = Math.min(...cues.map((cue) => cue.endTime));
  const duration = endTime - startTime;
  if (!Number.isFinite(duration) || duration <= 0) return 0;

  const progress = Math.min(1 - Number.EPSILON, Math.max(0, (currentTime - startTime) / duration));
  return Math.min(lastPage, Math.floor(progress * pageCount));
};

/** Keep small players to two or three lines, with more room in larger pictures. */
export const getCaptionViewportHeight = (pictureHeight: number, fontSize: number): number => {
  const height = Math.max(1, pictureHeight);
  const lineBudget = Math.min(6, Math.max(2, Math.floor(height / 180) + 1));
  // Match the rendered 1.3 line-height and the backdrop's two 2px paddings.
  const lineBudgetHeight = lineBudget * Math.max(1, fontSize) * 1.3 + 4;
  return Math.max(1, Number(Math.min(height * 0.25, lineBudgetHeight).toFixed(3)));
};
